import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";

interface SearchInputProps {
  allItems: string[];
  onSearchChange: (searchTerm: string) => void;
}

const SearchInputMobile: FC<SearchInputProps> = ({ allItems, onSearchChange }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isSuggestionsVisible, setIsSuggestionsVisible] = useState(false);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    onSearchChange(value);

    if (value) {
      const filteredSuggestions = allItems
        .filter((item) => item.toLowerCase().includes(value.toLowerCase()))
        .slice(0, 10);
      setSuggestions(filteredSuggestions);
      setIsSuggestionsVisible(true);
    } else {
      setSuggestions([]);
      setIsSuggestionsVisible(false);
    }
  };

  const handleClear = () => {
    setSearchTerm("");
    onSearchChange("");
    setSuggestions([]);
    setIsSuggestionsVisible(false);
  };

  const handleSuggestionClick = (itemName: string) => {
    setSearchTerm(itemName);
    onSearchChange(itemName);
    setIsSuggestionsVisible(false);
  };

  return (
    <div className="relative">
      <MagnifyingGlassIcon
        className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
      />
      <input
        type="text"
        placeholder={`${t("Search Teams")}`}
        value={searchTerm}
        onChange={handleSearchChange}
        onFocus={() => setIsSuggestionsVisible(true)}
        onBlur={() => setTimeout(() => setIsSuggestionsVisible(false), 150)}
        className="w-full px-3 py-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base pl-7 pr-8"
        autoComplete="off"
        style={{ 
          border: '0px', 
          minWidth: '250px',
        }}
      />
      {searchTerm && (
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            handleClear();
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2"
        >
          <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
        </button>
      )}
      {isSuggestionsVisible && suggestions.length > 0 && (
        <ul className="absolute top-full left-0 w-full bg-white border border-gray-300 rounded-md mt-1 shadow-lg z-10 max-h-48 overflow-y-auto">
          {suggestions.map((item, index) => (
            <li
              key={index}
              className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-base"
              onMouseDown={() => handleSuggestionClick(item)}
            >
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchInputMobile;